/**
 * useSiteDates — the hand-dated sites for the timeline.
 *
 * public/data/site-dates.json maps a feature id to {y0, y1, y2?, source}:
 * the earliest and latest year a site can have been built, an end of use
 * where one is known, and the publication the years come from. Register
 * periods are era-wide; these rows are where an archaeologist has said
 * more, and useGeoJsonLayers::splitByYear lets them win. Loaded once, the
 * first time the timeline opens, and never while it is closed; an empty
 * object until then, and on any failure, so the map falls back to the
 * registers rather than to nothing.
 */
import { useEffect, useState } from "react";
import type { SiteDate } from "./useGeoJsonLayers.ts";
import { warnFetch } from "../utils/logErr.ts";

let cache: Record<string, SiteDate> | null = null;
let inflight: Promise<Record<string, SiteDate>> | null = null;

export function loadSiteDates(): Promise<Record<string, SiteDate>> {
  if (cache) return Promise.resolve(cache);
  if (!inflight) {
    inflight = fetch("/data/site-dates.json")
      .then((r) => { if (!r.ok) throw new Error(`site-dates: ${r.status}`); return r.json() as Promise<{ sites: Record<string, SiteDate> }>; })
      .then((j) => { cache = j.sites ?? {}; return cache; })
      .catch((e) => { warnFetch("site-dates", e); inflight = null; return {}; });
  }
  return inflight;
}

/** For tests: forget the loaded table. */
export function _resetSiteDates(): void { cache = null; inflight = null; }

const EMPTY: Record<string, SiteDate> = {};

export function useSiteDates(active: boolean): Record<string, SiteDate> {
  const [dates, setDates] = useState<Record<string, SiteDate>>(cache ?? EMPTY);
  useEffect(() => {
    if (!active || cache) return;
    let live = true;
    loadSiteDates().then((d) => { if (live) setDates(d); });
    return () => { live = false; };
  }, [active]);
  return dates;
}
